import { Badge } from '@worldcoo/ui/components/badge';
import { useLingui } from '@lingui/react/macro';
import { getProjectStatus } from '@worldcoo/shared/utils';

function TickCircleIcon(props: { className?: string }) {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" fill="none" className={props.className} viewBox="0 0 16 16">
			<path
				fill="#0e7f0a"
				d="M8 14.667c3.667 0 6.667-3 6.667-6.667s-3-6.667-6.667-6.667-6.667 3-6.667 6.667 3 6.667 6.667 6.667"
			/>
			<path stroke="#fff" strokeLinecap="round" strokeLinejoin="round" d="m5.167 8 1.886 1.887 3.78-3.774" />
		</svg>
	);
}

/** The labels of a portal's Campaigns tab, in the order its filter offers them. */
export const portalCampaignStatuses = ['fundraising', 'scheduled', 'notLaunched', 'disabled', 'funded'] as const;
export type PortalCampaignStatus = (typeof portalCampaignStatuses)[number];
/** The global Projects list keeps its two labels. */
export type GlobalProjectStatus = 'fundraising' | 'notFundraising';
export type ProjectDisplayStatus = PortalCampaignStatus | GlobalProjectStatus;

type LinkedCampaign = { startDate?: string; endDate?: string; disabled: boolean };

// `paused` reads "Disabled": a campaign removed from the portal reading "Paused" looked like a failed action (TEC-3616).
// The global list keeps its two labels; only a portal's tab tells the other three states apart (TEC-3985).
export function getProjectDisplayStatus(
	project: { linkedCampaigns?: LinkedCampaign[] },
	view: 'global',
): GlobalProjectStatus;
export function getProjectDisplayStatus(
	project: { linkedCampaigns?: LinkedCampaign[] },
	view: 'portal',
): PortalCampaignStatus;
export function getProjectDisplayStatus(
	project: { linkedCampaigns?: LinkedCampaign[] },
	view: 'global' | 'portal',
): ProjectDisplayStatus;
export function getProjectDisplayStatus(
	project: { linkedCampaigns?: LinkedCampaign[] },
	view: 'global' | 'portal',
): ProjectDisplayStatus {
	const status = getProjectStatus(project);
	if (status === 'active') return 'fundraising';
	if (view === 'global') return 'notFundraising';
	if (status === 'funded') return 'funded';
	if (status === 'paused') return 'disabled';
	return notFundraisingOnPortal(project.linkedCampaigns ?? []);
}

/**
 * The shared derivation calls both "starts later" and "never launched" not fundraising. A campaign removed from the
 * portal before its start date will not fundraise when the date comes, so it reads Disabled rather than Scheduled.
 */
function notFundraisingOnPortal(campaigns: LinkedCampaign[]): 'scheduled' | 'notLaunched' | 'disabled' {
	const now = Date.now();
	const upcoming = campaigns.filter((c) => c.startDate !== undefined && new Date(c.startDate).getTime() > now);
	if (upcoming.some((c) => !c.disabled)) return 'scheduled';
	if (upcoming.length > 0) return 'disabled';
	return 'notLaunched';
}

export function ProjectStatusBadge(props: { linkedCampaigns?: LinkedCampaign[]; view?: 'global' | 'portal' }) {
	const { t } = useLingui();
	const view = props.view ?? 'portal';
	const displayStatus = getProjectDisplayStatus(props, view);

	if (displayStatus === 'funded') {
		return (
			<Badge variant="outline" className="gap-1 border-transparent bg-[rgba(176,238,173,0.25)] pl-1 text-[#0e7f0a]">
				<TickCircleIcon className="size-4!" />
				{t`Funded`}
			</Badge>
		);
	}

	if (displayStatus === 'fundraising') {
		return (
			<Badge variant="outline" className="border-transparent bg-green-100/50 text-green-700">
				{t`Fundraising`}
			</Badge>
		);
	}

	if (displayStatus === 'disabled') {
		return (
			<Badge variant="outline" className="border-transparent bg-amber-100/50 text-amber-700">
				{t`Disabled`}
			</Badge>
		);
	}

	if (displayStatus === 'scheduled') {
		return (
			<Badge variant="outline" className="border-transparent bg-blue-100/50 text-blue-700">
				{t`Scheduled`}
			</Badge>
		);
	}

	if (displayStatus === 'notLaunched') {
		return <Badge variant="outline">{t`Not launched`}</Badge>;
	}

	return <Badge variant="outline">{t`Not fundraising`}</Badge>;
}
